import { action, computed, observable } from 'mobx'
import { HttpService } from '@services/HttpService'
import { config } from '@config'
import { storage } from '@services/storageService'
import { filter, reduce } from 'lodash'
import { GROUP_NAMES_MAPPING } from '@constants'
import { stores } from '@stores'
import { ENHANCED_VIEW_ROUTES } from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import { View, ViewModes } from '@modules/order/constants/views'
import { ORDER_PAGES_NAME } from '@modules/wholesale/productCardConfigSettings/product-settings-constants'
import { ListTableView } from '@modules/order/stores/view/List/ListViewTableService'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import ClientTypeChannelMap from '@modules/common/models/enums/ClientTypeChannelMap'
import { DefaultConfig } from '@constants'
import { isRetailClient } from '@utils/get-channel-name'

export class ProductConfigurationStore {
  @observable configurationData = null
  @observable loadingConfig = false
  @observable loadedConfigData = false
  @observable maxGroupByAttributeRows = 5
  @observable appliedGroupFilterSort: any = {}
  private httpService = new HttpService()

  @computed get viewConfigurationData() {
    return this.configurationData?.views?.showroom
  }

  @computed get defaultProductsGroup() {
    return this.configurationData?.defaultProductsGroup || null
  }

  @computed get allAttributesFlattenObject() {
    const {
      queryParams: { orderId }
    } = stores.nav
    const flattenAttributes = this.getAllFlattenAttributes(
      orderId ? 'orderCollection' : ''
    )
    const temp = {}
    flattenAttributes.forEach(item => (temp[item.id] = item))
    return temp
  }

  @computed get defaultValuesFromConfig() {
    return this.configurationData?.defaultValues || {}
  }

  @computed get allMetricsAttributes() {
    const orderProductMetrics =
      this.configurationData?.businessEntities?.orderProduct?.metrics || []
    const planningBudgetMetrics =
      this.configurationData?.businessEntities?.orderProduct?.metrics || []
    return [...orderProductMetrics, ...planningBudgetMetrics]
  }

  @computed get enableEnhancedViewByDefault() {
    // This flag is used to load enhanced view by default
    return !!this.configurationData?.featureFlags?.enableEnhancedView
  }

  @computed get enhancedViewEnabled() {
    const {
      nav: {
        location: { pathname },
        queryParams: { view }
      }
    } = stores
    const orderPage =
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.orderCollection}`) &&
      (!view || view === View.Product || view === View.AddProduct || view || View.List)

    return !!(
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.collection}`) ||
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.wishlist}`) ||
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.basketAndOrders}`) ||
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.lookbook}`) ||
      orderPage
    )
  }

  /* This property will be used only for showing enhanced header
    with multilevel scroll on selected views 
  */
  @computed get enhancedHeaderEnabled() {
    const {
      nav: {
        location: { pathname },
        queryParams: { view }
      }
    } = stores
    const orderPage =
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.orderCollection}`) &&
      (!view || view === View.Product || view === View.AddProduct)

    return (
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.collection}`) ||
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.wishlist}`) ||
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.lookbook}`) ||
      orderPage
    )
  }

  @computed get isEnhancedBasketView() {
    const {
      nav: {
        location: { pathname }
      }
    } = stores
    return pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.basketAndOrders}`)
  }

  @computed get isEnhancedWishListPage() {
    const {
      nav: {
        location: { pathname }
      }
    } = stores
    return pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.wishlist}`)
  }

  @computed get defaultProductsGroupMapping() {
    const activityMapping = this.defaultProductsGroup?.['original-activity-name']
    if (activityMapping) {
      const temp = {}
      Object.keys(activityMapping).forEach(key => {
        if (activityMapping[key] && activityMapping[key].length) {
          temp[key] = activityMapping[key].map(name => GROUP_NAMES_MAPPING[name])
        }
      })
      return temp
    }
    return null
  }

  @computed get featureFlags() {
    return this.configurationData?.featureFlags
  }

  @computed get hideSplitQuantity() {
    return !!this.configurationData?.featureFlags?.wholesale?.hideSplitQuantity
  }

  @computed get isWarningModeActive() {
    const {
      nav: {
        queryParams: { viewMode, view, clientType, subSection }
      }
    } = stores

    return (
      viewMode === ViewModes.Warning &&
      view === View.List &&
      (clientType === getEnumValue(VIPClientType.Internal)
        ? !subSection || subSection === ListTableView.Quantity
        : true)
    )
  }

  @computed get initialAllocationEnabled() {
    const {
      nav: {
        queryParams: { clientType, view, subSection, viewMode }
      }
    } = stores
    return (
      this.featureFlags.initialAllocationEnabled &&
      isRetailClient(clientType) &&
      view === View.List &&
      subSection === ListTableView.Quantity &&
      viewMode !== ViewModes.Warning
    )
  }

  @computed get initialAllocationActivated() {
    if (!this.initialAllocationEnabled) {
      return false
    }
    const {
      nav: {
        queryParams: { viewMode }
      }
    } = stores
    return viewMode === ViewModes.InitialAllocation
  }

  @computed get defaultQuantityType() {
    const {
      nav: {
        queryParams: { clientType }
      }
    } = stores
    const channel = getEnumValue(
      ClientTypeChannelMap[
        clientType ? VIPClientType[clientType] : VIPClientType.External
      ]
    )
    return config.defaultQuantityType[channel] || DefaultConfig.QUANTITY_TYPE[channel]
  }

  @computed get dataExploreViewEnabled() {
    return this.featureFlags?.dataExploreViewEnabled
  }

  @computed get bulkProductTagBatchSize() {
    return this.configurationData?.defaultValues?.bulkProductTagBatchSize || 250
  }

  @action getFlattenedProductAttributes = (data = []) => {
    const flattenData = reduce(
      data,
      (acc, attribute) => {
        if (attribute.attributes.length) {
          let mapData = []
          mapData = this.getFlattenedProductAttributes(attribute?.attributes)
          acc.push(...mapData)
        } else {
          acc.push(attribute)
        }
        return acc
      },
      []
    )
    return flattenData
  }

  @action
  getActionAttributes = (
    viewAction: string,
    channel: string,
    view: string,
    flattenedList?: boolean
  ) => {
    const allAttributes = this.getAllFlattenAttributes(view)
    const dataOrganize = this.viewConfigurationData?.[channel]?.[view]?.dataOrganize
    const filterAttributes = filter(allAttributes, attribute =>
      dataOrganize?.[viewAction]?.includes(attribute?.id)
    )
    if (flattenedList) {
      return filterAttributes.map(attr => attr.id)
    }
    return filterAttributes
  }

  @action
  getGlobalTableSettingsByView = (view: string, subSection: string, channel: string) => {
    const tableSettings =
      this.viewConfigurationData?.[channel]?.[view]?.tableSettings?.[subSection]
    return tableSettings
  }

  @action
  getViewProductCardAttributes = (channelName, viewName) => {
    const temp =
      this.viewConfigurationData?.[channelName]?.[viewName]?.productCard?.attributes || []
    return temp
  }

  @action getProductDetailsQuantityViews = (
    channelName: string,
    viewName: string = 'orderCollection'
  ) => {
    const detailsAttributes =
      this.viewConfigurationData?.[channelName]?.[viewName]?.productDetails
    return detailsAttributes?.orderQuantity
  }

  @action
  getProductDetailsAttributes = (
    channel: string,
    isOrderProduct: boolean,
    allCombined?: boolean,
    subSection?: string
  ) => {
    const view = isOrderProduct ? 'orderCollection' : 'collection'
    const detailsAttributes =
      this.viewConfigurationData?.[channel]?.[view]?.productDetails
    if (allCombined) {
      return [
        ...(detailsAttributes?.moreAttributes?.attributes || []),
        ...(detailsAttributes?.overview?.attributes || [])
      ]
    }
    if (subSection) {
      return detailsAttributes?.[subSection]?.attributes || []
    }
    return detailsAttributes || {}
  }

  @action
  getOrderValidationWarningsAttributes = () => {
    const temp =
      this.configurationData?.businessConfiguration?.orderStatusChange?.confirmOrder
        ?.validationWarnings || []
    return [...temp]
  }

  @action
  findSpecificAttribute = attributeId => {
    if (attributeId) {
      return this.allAttributesFlattenObject?.[attributeId] || null
    }
    return null
  }

  @action
  getAllFlattenAttributes = (view: string) => {
    const buyingSessionProductAttributes = this.getFlattenedProductAttributes(
      this.configurationData?.businessEntities?.buyingSessionProduct?.attributes
    )
    if (ORDER_PAGES_NAME.includes(view)) {
      const orderProductAttributes = this.getFlattenedProductAttributes(
        this.configurationData?.businessEntities?.orderProduct?.attributes
      )
      const filteredOrderProductAttributes = orderProductAttributes.filter(
        elem => !buyingSessionProductAttributes.find(el => el.id === elem.id)
      )
      return [...buyingSessionProductAttributes, ...filteredOrderProductAttributes]
    } else {
      return buyingSessionProductAttributes
    }
  }

  @action fetchConfigurationData = () => {
    const url = `${config.buyingBoardServiceRestEndpoint}core/configuration`
    const accessToken: string = storage.getItem('access_token')
    this.loadingConfig = true
    this.httpService
      .get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
      .then(res => {
        if (res?.data) {
          this.configurationData = res.data
          this.loadedConfigData = true
        }
      })
      .finally(() => {
        this.loadingConfig = false
      })
  }

  @action
  updateViewObject = (action, data, view) => {
    const viewData = this.appliedGroupFilterSort?.[view]
    if (!viewData) {
      this.appliedGroupFilterSort[view] = {}
    }
    this.appliedGroupFilterSort[view][action] = data
  }

  @action
  saveAppliedGroupFilterSortSearch = (action: string, data: any, view: string) => {
    if (action && view) {
      switch (action) {
        case 'filter':
          this.updateViewObject(action, data, view)
          break
        case 'sort':
          this.updateViewObject(action, data, view)
          break
        case 'group':
          this.updateViewObject(action, data, view)
          break
        case 'search':
          this.updateViewObject(action, data, view)
          break
        case 'reset':
          const viewData = this.appliedGroupFilterSort?.[view]
          if (!viewData) {
            this.appliedGroupFilterSort[view] = {}
          }
          break
        case 'resetAll':
          this.appliedGroupFilterSort = {}
          break
      }
    }
  }
}

export const productConfiguration = new ProductConfigurationStore()
